"use client";
import { useAtomValue } from "jotai";
import { usePathname } from "next/navigation";
import { useState } from "react";
import type { PopupVisibility } from "@/api/AppApi/services/company/interfaces/ICompany";
import { usePopupOrderCount } from "@/api/AppApi/services/order/usePopupOrderCount";
import { isSignedInAtom } from "@/hooks/authAtoms";
import { useTranslation } from "@/hooks/localization/useTranslations";
import useIsClient from "@/hooks/useIsClient";
import { Modal } from "@/utils/bootstrap";
import { getAccessToken } from "@/utils/functions/cookieOperations";
import { safeHTML } from "@/utils/functions/safeHTML";
import {
  matchesPopupVisibility,
  needsPopupOrderCount,
} from "@/utils/helpers/popup-visibility";
import { i18n } from "../../../../i18n-config";

interface Props {
  content: string;
  visibility?: readonly PopupVisibility[] | null;
}

const StartStoreMessageModal: React.FC<Props> = ({ content, visibility }) => {
  const pathname = usePathname();
  const isClient = useIsClient();
  const [dismissed, setDismissed] = useState(false);
  const authState = useAtomValue(isSignedInAtom);
  // The cookie covers the hydration window before SyncAuthState updates the atom.
  const signedIn = isClient && (authState || !!getAccessToken());
  const t = useTranslation();
  const canShow = isClient && isHomePage(pathname) && !dismissed;
  const orders = usePopupOrderCount(
    canShow && needsPopupOrderCount(visibility, signedIn),
  );
  const orderCount =
    orders.isSuccess && !orders.isFetching ? orders.data : undefined;
  const shouldShow =
    canShow && matchesPopupVisibility(visibility, signedIn, orderCount);
  const titleId = "start-store-message-title";
  if (!shouldShow) return null;
  return (
    <Modal
      centered
      show={shouldShow}
      dialogClassName="messagebox-dialog"
      size="lg"
      contentClassName="w-auto m-0"
      onHide={() => setDismissed(true)}
      scrollable
      aria-labelledby={titleId}
    >
      <Modal.Header className="hide-to-vistor">
        <Modal.Title id={titleId}>{t.welcome}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div
          dangerouslySetInnerHTML={{
            __html: safeHTML(content),
          }}
        ></div>
      </Modal.Body>
    </Modal>
  );
};

export default StartStoreMessageModal;
const isHomePage = (url: string) => {
  const urlTerms = url.split("/");
  if (urlTerms.length === 2 && ["", ...i18n.locales].includes(urlTerms[1]))
    return true;
  return false;
};
